'use client';

import { forwardRef, useId } from 'react';
import clsx from 'clsx';
import type { FieldProps } from './types';

function FieldComponent(
    {
        label,
        error,
        hint,
        classNames = {},
        className,
        as,
        id: idProp,
        ...rest
    }: FieldProps,
    ref: React.Ref<HTMLInputElement | HTMLTextAreaElement>
) {
    const generatedId = useId();
    const isTextarea = as === 'textarea';
    const id = idProp ?? (label ? `field-${label.replace(/\s+/g, '-').toLowerCase()}` : generatedId);
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;
    const merged = {
        root: classNames.root,
        label: classNames.label,
        input: classNames.input,
        error: classNames.error,
        hint: classNames.hint,
    };

    const inputProps = {
        ...rest,
        id,
        ref: ref as React.Ref<HTMLInputElement & HTMLTextAreaElement>,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': [error && errorId, hint && !error && hintId].filter(Boolean).join(' ') || undefined,
        className: merged.input,
    };

    return (
        <div className={clsx(merged.root, className)}>
            {label && (
                <label htmlFor={id} className={merged.label}>
                    {label}
                </label>
            )}
            {isTextarea ? (
                <textarea {...(inputProps as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} />
            ) : (
                <input {...(inputProps as React.InputHTMLAttributes<HTMLInputElement>)} />
            )}
            {error && (
                <p id={errorId} role="alert" className={merged.error}>
                    {error}
                </p>
            )}
            {hint && !error && (
                <p id={hintId} className={merged.hint}>
                    {hint}
                </p>
            )}
        </div>
    );
}

const Field = forwardRef(FieldComponent);
Field.displayName = 'Field';

export default Field;
